import React, { Fragment } from 'react';
import { observable, action, computed } from 'mobx';
import { inject, Observer } from 'mobx-react';
import { CSVReader } from 'react-papaparse';
import moment from 'moment-timezone';
import _ from 'lodash';

import { Panel, PanelType } from 'office-ui-fabric-react/lib/Panel';
import {
  DefaultButton,
  PrimaryButton,
} from 'office-ui-fabric-react/lib/Button';
import {
  MessageBar,
  MessageBarType,
} from 'office-ui-fabric-react/lib/MessageBar';

import { localizable } from 'components/Localization';

import CsvMappingTable from './CsvMappingTable';
import BulkRegistrationTable from './BulkRegistrationTable';
import BulkRegistrationPanelBase from './BulkRegistrationPanelBase';
import BulkRegistrationStore from './BulkRegistrationStore';
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import { getDefaultUploadTypes } from 'helpers/uploadTypes';

const maxDataShow = 5; // max number of rows in the mapping preview

const getAt = (arr, index, defaultValue = null) =>
  index >= 0 && index < arr.length ? arr[index] : defaultValue;

const encodings = ['UTF-8', 'ISO-8859-1', 'ISO-8859-2'];

@inject('app', 'user', 'sale')
@localizable
class BulkRegistrationCsvPanel extends BulkRegistrationPanelBase {
  csvReaderRef;

  @observable encoding = 'UTF-8';

  fields = {}; // fields[<field key>] = { field properties from config schema }

  @observable loading = false;
  @observable loaded = false;
  @observable enableBackToMapping = false;

  @observable data = observable([]); // raw loaded data

  @observable mapping = observable([]); // csv field mapping, mapping[<column index (in csv data)>] = <field key> | ''

  // inverseMapping[<field key>] = [array of <column index (in csv data)>]
  @computed get inverseMapping() {
    return _.mapValues(this.fields, (value, key) =>
      this.mapping
        .map((_key, _index) => (key === _key ? _index : -1))
        .filter((_index) => _index >= 0)
    );
  }

  @computed get mappedFieldsCount() {
    const inverseMapping = this.inverseMapping;
    return _.filter(
      this.fields,
      (field, key) => inverseMapping[key] && inverseMapping[key].length > 0
    ).length;
  }

  @computed get areAllMainFieldsMapped() {
    const inverseMapping = this.inverseMapping;
    return !_.some(
      this.fields,
      (field, key) =>
        !field.additional &&
        !(field.target instanceof Array && field.target.length === 0) &&
        (!inverseMapping[key] || inverseMapping[key].length === 0)
    );
  }

  @observable errors = observable([]);

  reset() {
    this.store.reset();
    this.loading = false;
    this.loaded = false;
    this.editing = false;
    this.enableBackToMapping = false;
    this.data.clear();
    this.mapping.clear();
    this.errors.clear();
  }

  componentDidMount() {
    const { user, sale, usedEmails, t } = this.props;
    this.store = new BulkRegistrationStore(
      user,
      sale,
      sale.round,
      usedEmails,
      t
    );

    const config = sale.root.saleConfig;
    this.reset();
    this.fields = config.getRegistrationScheme(sale, t);

    if (
      config.csvImportDefaultEncoding &&
      encodings.includes(config.csvImportDefaultEncoding)
    ) {
      this.encoding = config.csvImportDefaultEncoding;
      const self = this;
      setTimeout(function () {
        self.handleOpenDialog(document.createEvent('Event'));
      }, 250);
    }
  }

  @computed get dataReduced() {
    return this.data.length > maxDataShow
      ? _.take(this.data, maxDataShow).concat([['...']])
      : this.data;
  }

  handleOpenDialog = (e) => {
    if (this.csvReaderRef) {
      this.csvReaderRef.open(e);
      this.reset();
    }
  };

  encodingChange = (ev, option) => {
    this.encoding = option.key;
  };

  @action handleOnFileLoad = (data) => {
    this.reset();

    let maxcolcount = 0;
    this.mapping.clear();

    _.forEach(data, (row, index) => {
      if (row.errors.length > 0) {
        _.forEach(row.errors, (error) => {
          this.errors.push(`[${index + 1}] ${error.message}`);
        });
      }
      if (index === 0) {
        // assume the first line to be headers and try to identify column mapping
        this.resolveColumnMapping(row.data);
        this.enableBackToMapping = !this.areAllMainFieldsMapped;
        if (this.mappedFieldsCount === 0) this.data.push(row.data); // no column has been mapped -> assume no headers in the first row
      } else this.data.push(row.data);
      if (row.data.length > maxcolcount) maxcolcount = row.data.length;
    });

    this.loaded = true;

    // if all main fields are mapped, continue with import
    if (this.areAllMainFieldsMapped) this.handleImport();
  };

  resolveColumnMapping = (data) => {
    this.mapping.clear();
    data.forEach((value) => {
      let mappedKey = '';
      if (value) {
        // assume the value from the input row to be a header
        const _value = value.toUpperCase();
        _.forOwn(this.fields, (field, key) => {
          // check our fields and try to match the header to one of the defined field header candidates
          const header = _.find(
            field.headers,
            (_header) => _header.toUpperCase() === _value
          );
          // there can multiple columns mapped to the same field
          if (header && /*!this.mapping.includes(key) &&*/ !mappedKey) {
            // we found a candidate which is not yet mapped
            mappedKey = key;
          }
        });
      }
      this.mapping.push(mappedKey);
    });
  };

  handleOnError = (err, file, inputElem, reason) => {
    this.errors.push(reason);
  };

  handleImport = () => {
    // convert each imported row into our import object using the mapping
    const inverseMapping = this.inverseMapping;
    this.data.forEach((_data) => {
      let _item = _.mapValues(this.fields, (field, key) => {
        // pick the first non-empty csv field that are mapped to this field
        const candidates = inverseMapping[key]
          .map((index) => getAt(_data, index, ''))
          .filter((value) => !!value);
        let value = candidates.length > 0 ? candidates[0] : '';
        //if (typeof field.import === 'function')
        //  value = field.import(value, _data);
        return value;
      });
      // apply 'import' functions
      _.forOwn(this.fields, (field, key) => {
        if (typeof field.import === 'function')
          _item[key] = field.import(_item[key], _item, _data);
      });
      _item.requiredUploads = getDefaultUploadTypes(
        this.props.sale.root.saleConfig,
        this.props.sale.offerData
      );
      this.store.add(_item);
    });

    this.editing = true;
  };

  renderFormatedDate = (date) => {
    return new Date(date).toFormatedDateString('L');
  };

  parseDateFromString = (value) => {
    return !!value ? moment(value, 'DD.MM.YYYY').toDate() : value;
  };

  handleBackToMapping = () => {
    this.editing = false;
    this.store.items.clear();
  };

  handleSave = async () => {
    const { onClose } = this.props;
    await this.store.save();
    //this.enableBackToMapping = false; // you can't go back once you tried to upload the registrations
    // if nothing's left -> close the panel
    if (!_.find(this.store.items, (item) => !this.store.isItemEmpty(item))) {
      if (onClose) onClose(); // only if there's no item left
    }
  };

  @computed get canSave() {
    return (
      !this.importing &&
      this.editing &&
      !this.store.saving &&
      this.store.validItemsCount > 0
    );
  }

  renderPanelFooterContentAdditional = () => {
    const { t } = this.props;
    return (
      <Fragment>
        {this.loaded && this.editing && this.enableBackToMapping && (
          <DefaultButton
            onClick={this.handleBackToMapping}
            disabled={false}
            style={{ marginRight: '8px', alignSelf: 'flex-end' }}
            iconProps={{ iconName: 'Back' }}
          >
            {t('Back to mapping')}
          </DefaultButton>
        )}
      </Fragment>
    );
  };

  render() {
    const { t, sale, usedEmails } = this.props;

    return (
      <Panel
        headerText={t('Import from CSV file')}
        hasCloseButton={false}
        type={PanelType.smallFluid}
        isOpen={this.props.isOpen}
        onDismiss={this.handleClose}
        onRenderFooterContent={() => this.renderPanelFooterContent()}
        isFooterAtBottom={true}
        closeButtonAriaLabel="Close"
        styles={{
          content: {
            height: '100%',
            paddingBottom: '0',
          },
        }}
      >
        <Observer
          render={() => (
            <Fragment>
              {!this.loaded &&
                !sale.root.saleConfig.csvImportDefaultEncoding && (
                  <div className="ms-Grid">
                    <div className="ms-Grid-row">
                      <div className="ms-Grid-col ms-sm2">
                        <Dropdown
                          label={t('Encoding')}
                          selectedKey={this.encoding}
                          options={encodings.map((encoding) => {
                            return { key: encoding, text: encoding };
                          })}
                          onChange={this.encodingChange}
                        />
                      </div>
                    </div>
                  </div>
                )}
              <CSVReader
                disableClick
                ref={(node) => {
                  this.csvReaderRef = node;
                }}
                onFileLoad={this.handleOnFileLoad}
                onError={this.handleOnError}
                noClick={true}
                isReset={true}
                config={{
                  dynamicTyping: false,
                  skipEmptyLines: true,
                  encoding: this.encoding,
                }}
              >
                {({ file }) => (
                  <Fragment>
                    {!this.loaded && (
                      <div className="ms-Grid">
                        <div
                          className="ms-Grid-row"
                          style={{ paddingTop: '10px', paddingBottom: '10px' }}
                        >
                          <div className="ms-Grid-col ms-sm12">
                            <PrimaryButton
                              onClick={this.handleOpenDialog}
                              disabled={false}
                            >
                              {`1. ${t('Upload')}`}
                            </PrimaryButton>
                          </div>
                        </div>
                      </div>
                    )}
                  </Fragment>
                )}
              </CSVReader>

              {this.errors.length > 0 && (
                <div className="ms-Grid">
                  <div className="ms-Grid-row" style={{ padding: '10px 0' }}>
                    <div className="ms-Grid-col ms-sm12">
                      <MessageBar messageBarType={MessageBarType.error}>
                        {this.errors.map((error, index) => (
                          <div key={index}>{error}</div>
                        ))}
                      </MessageBar>
                    </div>
                  </div>
                </div>
              )}

              {this.loaded && !this.editing && (
                <div className="ms-Grid">
                  <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12">
                      <ul>
                        <li>
                          <strong>
                            {t('Total number of rows: {count}', {
                              count: this.data.length,
                            })}
                          </strong>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12">
                      <CsvMappingTable
                        data={this.dataReduced}
                        fields={this.fields}
                        mapping={this.mapping}
                      />
                    </div>
                  </div>
                  <div className="ms-Grid-row" style={{ paddingTop: '10px' }}>
                    <div className="ms-Grid-col ms-sm12">
                      <PrimaryButton
                        onClick={this.handleImport}
                        disabled={false}
                      >
                        {`2. ${t('Import')}`}
                      </PrimaryButton>
                    </div>
                  </div>
                </div>
              )}

              {this.loaded && this.editing && (
                <Fragment>
                  {this.renderRegistrationResult()}
                  <BulkRegistrationTable
                    store={this.store}
                    usedEmails={usedEmails}
                  />
                </Fragment>
              )}
            </Fragment>
          )}
        />
      </Panel>
    );
  }
}

export default BulkRegistrationCsvPanel;



// WEBPACK FOOTER //
// ./src/app/containers/Sale/OffersSection/BulkRegistration/BulkRegistrationCsvPanel.jsx